import React, {useState, useEffect, useContext} from 'react';
import {Form, Radio, DatePicker, Select, Divider, Checkbox, Input, Row, Col} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import GenericDialog from '@common/GenericDialog';
import dayjs from 'dayjs';
import axios from "@common/axios";
import SyllabusTopicConceptSelect, {SELECT_LEVELS} from "@common/SyllabusTopicConceptSelect";
import Button from "@common/Button";
import MathJaxHTMLContent from "@common/MathJaxHTMLContent";
import RecipientSelect, {RECIPIENT_OBJ} from "@common/RecipientSelect";
import CircularLoader from "@common/CircularLoader";
import {UserContext} from "@context/UserContext";
import {BrandContext} from "@context/BrandContext";

const EditTrailAssignmentDialog = props => {
    const {open, onClose, setReload, trailAssignment} = props;
    const {user} = useContext(UserContext);
    const brandConfig = useContext(BrandContext);

    const CONTENT_OBJ = {
        SINGLE_CONCEPT: {name: brandConfig.conceptName, val: 'SINGLE_CONCEPT', inputLabel: brandConfig.conceptName, inputName: 'conceptIds', selectLevel: SELECT_LEVELS.CONCEPT},
        MULTIPLE_CONCEPT: {name: 'Multiple ' + brandConfig.conceptsName, val: 'MULTIPLE_CONCEPT', inputLabel: brandConfig.conceptsName, inputName: 'conceptIds', selectLevel: SELECT_LEVELS.CONCEPT},
        SINGLE_TOPIC: {name: brandConfig.topicName, val: 'SINGLE_TOPIC', inputLabel: brandConfig.topicName, inputName: 'topicId', selectLevel: SELECT_LEVELS.TOPIC}
    }
    const CONTENT_ARR = [CONTENT_OBJ.SINGLE_CONCEPT, CONTENT_OBJ.MULTIPLE_CONCEPT, CONTENT_OBJ.SINGLE_TOPIC];
    const MAX_SELECTED_CONCEPTS = 10;

    const isConceptSpecific = trailAssignment?.conceptSpecific;
    const isMultiConcept = trailAssignment?.multiConcept;

    const [editTrailAssignmentForm] = Form.useForm();
    const [contentType, setContentType] = useState(CONTENT_OBJ.SINGLE_CONCEPT);
    const [trailConcepts, setTrailConcepts] = useState({});
    const [selectedConcepts, setSelectedConcepts] = useState([]);
    const [selectedConcept, setSelectedConcept] = useState(null);
    const [selectedConceptIds, setSelectedConceptIds] = useState([]);
    const [recipientObj, setRecipientObj] = useState(null);
    const [classes, setClasses] = useState([]);
    const assignedStartDate = dayjs(trailAssignment?.startDate);

    const defaultRecipientSelectObj = {studentIds: trailAssignment?.studentIds, classIds: trailAssignment?.classIds, recipientType: trailAssignment?.classIds.length > 1 ? RECIPIENT_OBJ.MULTIPLE_CLASSES : RECIPIENT_OBJ.SINGLE_CLASS};

    useEffect(() => {
        if (trailAssignment) {
            axios.get('/web/ui/myClasses/viewMyClasses')
                .then(response => {
                    setClasses(response.data.filter(clazz => clazz.totalStudents > 0));
                });
            if (isConceptSpecific && !trailConcepts.hasOwnProperty(trailAssignment?.pathwayAssignmentId)) {
                axios.get('/web/ui/trail/trailConcepts/' + trailAssignment?.pathwayId)
                    .then((response) => {
                        if (response.data) {
                            setTrailConcepts({[trailAssignment?.pathwayAssignmentId]: response.data});
                        }
                    }).catch((error) => {
                        console.error('Error loading concept with tab hash: ',error);
                    });
            } else if (isMultiConcept && trailAssignment?.concepts) {
                setSelectedConceptVals(trailAssignment.concepts);
            }

            const tempContentType = trailAssignment?.topic ? CONTENT_OBJ.SINGLE_TOPIC : isMultiConcept ? CONTENT_OBJ.MULTIPLE_CONCEPT : CONTENT_OBJ.SINGLE_CONCEPT;

            setContentType(tempContentType);
            editTrailAssignmentForm.setFieldsValue({
                startDate: dayjs(),
                trailAssignmentName: trailAssignment.pathwayAssignmentName,
                message: trailAssignment.message,
                contentType: tempContentType.val
            });
        } else {
            editTrailAssignmentForm.resetFields();
        }
    }, [trailAssignment]);

    const handleAddConcept = () => {
        setSelectedConceptVals([selectedConcept, ...selectedConcepts]);
    }

    const onSelectedConceptsChange = newConcepts => {
        setSelectedConceptVals(selectedConcepts.filter(concept => newConcepts.includes(concept.conceptId)));
    }

    const setSelectedConceptVals = concepts => {
        const ids = concepts.map(concept => concept.conceptId);
        setSelectedConcepts(concepts);
        setSelectedConceptIds(ids);

        editTrailAssignmentForm.setFieldValue('conceptIds', ids);
        editTrailAssignmentForm.validateFields(['conceptIds']);
    }

    const onContentChange = ({target: {value}}) => {
        setContentType(CONTENT_OBJ[value]);
    }

    const onChangeSTC = contentObj => {
        if (contentObj && contentObj.conceptId) {
            setSelectedConcept(contentObj);
        }
    }

    const handleEditTrailAssignment = async () => {
        editTrailAssignmentForm.validateFields()
            .then(values => {
                const params = {trailId: trailAssignment?.pathwayId, trailAssignmentId: trailAssignment?.pathwayAssignmentId, ...values};

                params.startDate = params.startDate.format(user.getDateTimeFormatString());

                if (params.contentType === CONTENT_OBJ.SINGLE_CONCEPT.val) {
                    params.conceptIds = [params.conceptId];
                }
                if (params.contentType !== CONTENT_OBJ.SINGLE_TOPIC.val) {
                    delete params.topicId;
                }

                params.recipientGroup = recipientObj?.recipientGroup?.val;

                delete params.syllabusGroupId;
                delete params.syllabusId;
                delete params.conceptId;
                delete params.recipient;
                delete params.contentType;

                axios.post('/web/ui/trail/updateTrailAssignment', params, {headers: {'Content-Type': 'application/x-www-form-urlencoded'}})
                    .then(response => {
                        if (response.data.success) {
                            displayToastMessage(brandConfig.taskName + ' updated successfully', null, 'success', undefined, 10000);

                            editTrailAssignmentForm.resetFields();
                            if (typeof setReload === 'function') setReload();
                            if (typeof onClose === 'function') onClose();
                        } else {
                            displayToastMessage('Error assigning test', response.data.message, 'error', undefined, 10000);
                        }
                    }).catch(error => {
                        console.error(error);
                    });
            });
    }

    return (
        <GenericDialog
            title={'Edit ' + brandConfig.taskName + ' assignment'}
            open={open}
            contentStyle={{width: 630}}
            onClose={onClose}
            buttons={[
                {
                    name: 'Cancel',
                    extraProps: {type: 'text'},
                },
                {
                    name: 'Update',
                    callback: handleEditTrailAssignment,
                }
            ]}
        >
            <Form
                className={'ch-form'}
                form={editTrailAssignmentForm}
                labelCol={{span: 6}}
                wrapperCol={{span: 18}}
            >
                {trailAssignment?.pathwayAssignmentName &&
                    <h2>{trailAssignment.pathwayAssignmentName}</h2>
                }
                {isConceptSpecific &&
                    <>
                        <div>This {brandConfig.taskName} contains content from:</div>
                        {!trailConcepts.hasOwnProperty(trailAssignment?.pathwayAssignmentId) &&
                            <CircularLoader pageCentered={true} displayInElement={true} spacing={false} />
                        }
                        {trailConcepts.hasOwnProperty(trailAssignment?.pathwayAssignmentId) &&
                            <ul className={'ch-ellipsis-list'} style={{maxHeight: '150px'}}>
                                {trailConcepts[trailAssignment.pathwayAssignmentId].map(concept => {
                                    return <li key={concept.conceptId}>{brandConfig.conceptName}</li>
                                })}
                            </ul>
                        }
                    </>
                }
                {!isConceptSpecific &&
                    <>
                        <Form.Item label={'Content'} name={'contentType'}>
                            <Radio.Group
                                optionType={'button'}
                                onChange={onContentChange}
                                options={CONTENT_ARR.map(contentType => {
                                    return {label: contentType.name, value: contentType.val}
                                })}
                            />
                        </Form.Item>
                        <SyllabusTopicConceptSelect
                            form={editTrailAssignmentForm}
                            selectLevel={contentType.selectLevel}
                            onChange={onChangeSTC}
                            initialIds={isConceptSpecific ? undefined : {
                                syllabusId: contentType.val === CONTENT_OBJ.SINGLE_TOPIC.val ? trailAssignment?.topic?.syllabusId : contentType.val === CONTENT_OBJ.MULTIPLE_CONCEPT.val ? trailAssignment?.concepts[0]?.syllabusId : trailAssignment?.concept?.syllabusId,
                                topicId: contentType.val === CONTENT_OBJ.SINGLE_TOPIC.val ? trailAssignment?.topic?.topicId : contentType.val === CONTENT_OBJ.MULTIPLE_CONCEPT.val ? trailAssignment?.concepts[0]?.topicId : trailAssignment?.concept?.topicId,
                                conceptId: contentType.val === CONTENT_OBJ.SINGLE_TOPIC.val ? undefined : contentType.val === CONTENT_OBJ.MULTIPLE_CONCEPT.val ? trailAssignment?.concepts[0]?.conceptId : trailAssignment?.concept?.conceptId,
                            }}
                        />
                        {contentType.val === CONTENT_OBJ.MULTIPLE_CONCEPT.val &&
                            <>
                                <Form.Item label={'Selected ' + brandConfig.conceptsName} name={'conceptIds'}
                                           rules={[
                                               {
                                                   validator: () => {
                                                       console.log('selectedConceptIds',selectedConceptIds);
                                                       if (selectedConceptIds.length === 0) {
                                                           return Promise.reject('Please select at least one ' + brandConfig.conceptName + '.');
                                                       }
                                                       return Promise.resolve();
                                                   },
                                               },
                                           ]}
                                >
                                    <div className={'flexColumn'}>
                                        <div className={'flexAlignCentre'}>
                                            <Select
                                                virtual={false}
                                                showSearch={false}
                                                allowClear
                                                autoClearSearchValue
                                                mode={'multiple'}
                                                maxTagCount={'responsive'}
                                                value={selectedConceptIds}
                                                onChange={onSelectedConceptsChange}
                                                notFoundContent={<div className={'flexCentre'}>No {brandConfig.conceptsName} selected</div>}
                                            >
                                                {selectedConcepts.map(concept => {
                                                    return (
                                                        <Select.Option key={concept.conceptId + '-s'} value={concept.conceptId}>
                                                            <MathJaxHTMLContent html={brandConfig.conceptName} />
                                                        </Select.Option>
                                                    );
                                                })}
                                            </Select>
                                            <Button htmlType={'button'} className={'margin-l--m'} aria-label={'Add ' + brandConfig.conceptsName}
                                                    disabled={selectedConcepts.length === MAX_SELECTED_CONCEPTS || selectedConceptIds.includes(selectedConcept?.conceptId)}
                                                    onClick={handleAddConcept}>Add {brandConfig.conceptName}</Button>
                                        </div>
                                        <span className={'ch-text-emphasis margin-t--m padding-l--s'}>{selectedConcepts.length} of {MAX_SELECTED_CONCEPTS} {brandConfig.conceptsName} selected</span>
                                    </div>
                                </Form.Item>
                            </>
                        }
                    </>
                }
                <Divider className={'margin--rm'} />
                <Form.Item
                    label={'Start date'}
                    name={'startDate'}
                    rules={[
                        {
                            required: true,
                            message: 'Please select a start date.',
                        },
                        {
                            validator: (_, value) => {
                                if (assignedStartDate && value && value < assignedStartDate) {
                                    return Promise.reject(`Start date must not be before (${assignedStartDate.format(user.getDateTimeFormatString() + ', HH:mm')}).`);
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <DatePicker
                        showTime={{
                            format: 'HH:mm',
                        }}
                        format={user.getDateTimeFormatString()}
                        placement={'topLeft'}
                        style={{width: '100%'}}
                    />
                </Form.Item>
                <Form.Item label={brandConfig.taskName + ' name'} name={'trailAssignmentName'} rules={[{ required: true, message: `Please enter a ${brandConfig.taskName.toLowerCase()} name.` }]}>
                    <Input />
                </Form.Item>
                <Form.Item label={'Message for students'} name={'message'}>
                    <TextArea rows={4}/>
                </Form.Item>
                <RecipientSelect form={editTrailAssignmentForm} classes={classes} setRecipientObj={setRecipientObj} defaultRecipientVals={defaultRecipientSelectObj} />
            </Form>
        </GenericDialog>
    );
};

export default EditTrailAssignmentDialog;